import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Save, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, refreshContent, formatApiError } from "@/lib/api";

const TABS = ["Enquiries", "Content", "Products", "Achievements", "Certifications", "Locations", "News", "Rates", "Media"];

const inputCls =
  "w-full border border-white/15 bg-[#0A0A0A] px-4 py-2.5 text-sm text-white placeholder:text-neutral-600 outline-none transition-colors duration-300 focus:border-brand";
const labelCls = "mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500";

function EnquiriesTab() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/enquiries").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const toggle = async (item) => {
    await api.patch(`/admin/enquiries/${item.id}`, { status: item.status === "new" ? "read" : "new" });
    load();
  };

  if (!items) return <p className="font-mono text-xs text-neutral-500">Loading…</p>;
  if (items.length === 0) return <p className="text-sm text-neutral-500" data-testid="enquiries-empty">No enquiries yet.</p>;

  return (
    <div className="space-y-4" data-testid="enquiries-list">
      {items.map((q) => (
        <div key={q.id} className="border border-white/10 bg-[#0C0C0C] p-6" data-testid={`enquiry-${q.id}`}>
          <div className="flex flex-wrap items-center gap-4">
            <span className={`px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] ${q.status === "new" ? "bg-brand text-white" : "bg-white/10 text-neutral-400"}`}>
              {q.status}
            </span>
            <p className="font-medium text-white">{q.name}{q.company ? ` — ${q.company}` : ""}</p>
            <p className="ml-auto font-mono text-[10px] text-neutral-500">{new Date(q.created_at).toLocaleString()}</p>
          </div>
          <div className="mt-3 grid gap-2 font-mono text-xs text-neutral-400 sm:grid-cols-2">
            <p>{q.email} · {q.phone || "no phone"}</p>
            <p>{q.product || "General"}{q.quantity ? ` · ${q.quantity}` : ""}</p>
          </div>
          {q.message && <p className="mt-3 border-t border-white/5 pt-3 text-sm text-neutral-400">{q.message}</p>}
          <button
            onClick={() => toggle(q)}
            data-testid={`enquiry-toggle-${q.id}`}
            className="mt-4 border border-white/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400 transition-colors hover:border-brand hover:text-brand"
          >
            Mark as {q.status === "new" ? "read" : "new"}
          </button>
        </div>
      ))}
    </div>
  );
}

function ContentTab() {
  const [content, setContent] = useState(null);
  useEffect(() => { api.get("/content").then((r) => setContent(r.data)).catch(() => setContent({})); }, []);
  if (!content) return <p className="font-mono text-xs text-neutral-500">Loading…</p>;

  const update = (key, field, value) => setContent((c) => ({ ...c, [key]: { ...c[key], [field]: value } }));
  const save = async (key) => {
    try {
      await api.put(`/admin/content/${key}`, { data: content[key] });
      refreshContent();
      toast.success(`${key} saved`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const Section = ({ title, children, sectionKey }) => (
    <div className="border border-white/10 bg-[#0C0C0C] p-6" data-testid={`content-section-${sectionKey}`}>
      <div className="mb-6 flex items-center justify-between">
        <h3 className="font-display text-xl font-semibold uppercase tracking-normal">{title}</h3>
        <button onClick={() => save(sectionKey)} data-testid={`content-save-${sectionKey}`} className="inline-flex items-center gap-2 bg-brand px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white transition-colors hover:bg-brand-deep">
          <Save className="h-3.5 w-3.5" /> Save
        </button>
      </div>
      <div className="space-y-5">{children}</div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="content-tab">
      <Section title="Hero" sectionKey="hero">
        <div><label className={labelCls}>Kicker</label><input className={inputCls} value={content.hero.kicker} onChange={(e) => update("hero", "kicker", e.target.value)} data-testid="content-hero-kicker" /></div>
        {content.hero.lines.map((line, i) => (
          <div key={i}>
            <label className={labelCls}>Headline line {i + 1}{i === content.hero.accent_index ? " (amber)" : ""}</label>
            <input
              className={inputCls}
              value={line}
              data-testid={`content-hero-line-${i}`}
              onChange={(e) => {
                const lines = [...content.hero.lines];
                lines[i] = e.target.value;
                update("hero", "lines", lines);
              }}
            />
          </div>
        ))}
        <div><label className={labelCls}>Subtitle</label><textarea rows={3} className={inputCls} value={content.hero.subtitle} onChange={(e) => update("hero", "subtitle", e.target.value)} data-testid="content-hero-subtitle" /></div>
        <div><label className={labelCls}>Background image URL</label><input className={inputCls} value={content.hero.image} onChange={(e) => update("hero", "image", e.target.value)} data-testid="content-hero-image" /></div>
      </Section>
      <Section title="About Intro" sectionKey="about">
        <div><label className={labelCls}>Intro paragraph</label><textarea rows={4} className={inputCls} value={content.about.intro} onChange={(e) => update("about", "intro", e.target.value)} data-testid="content-about-intro" /></div>
      </Section>
      <Section title="Contact Details" sectionKey="contact">
        <div><label className={labelCls}>Address</label><input className={inputCls} value={content.contact.address} onChange={(e) => update("contact", "address", e.target.value)} data-testid="content-contact-address" /></div>
        <div><label className={labelCls}>Phones (comma separated)</label><input className={inputCls} value={content.contact.phones.join(", ")} onChange={(e) => update("contact", "phones", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} data-testid="content-contact-phones" /></div>
        <div><label className={labelCls}>Emails (comma separated)</label><input className={inputCls} value={content.contact.emails.join(", ")} onChange={(e) => update("contact", "emails", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} data-testid="content-contact-emails" /></div>
      </Section>
      <Section title="Call To Action" sectionKey="cta">
        <div><label className={labelCls}>Title</label><input className={inputCls} value={content.cta.title} onChange={(e) => update("cta", "title", e.target.value)} data-testid="content-cta-title" /></div>
        <div><label className={labelCls}>Text</label><textarea rows={2} className={inputCls} value={content.cta.text} onChange={(e) => update("cta", "text", e.target.value)} data-testid="content-cta-text" /></div>
      </Section>
    </div>
  );
}

const EMPTY_PRODUCT = { name: "", category: "Bituminous Products", standard: "", packaging: "", description: "", applicationsCsv: "", tablesJson: "[]" };

const ACHIEVEMENT_LIST = {
  emptyItem: { year: "", tag: "MILESTONE", title: "", description: "", image: "" },
  fields: [
    { key: "year", label: "Year" },
    { key: "tag", label: "Tag (AWARD / APPROVAL / …)" },
    { key: "title", label: "Title", full: true },
    { key: "description", label: "Description", full: true, textarea: true },
    { key: "image", label: "Image path (optional, e.g. /awards/amea-2023.jpg)", full: true, required: false },
  ],
};

const CERTIFICATION_LIST = {
  emptyItem: { name: "", detail: "", number: "", validity: "" },
  fields: [
    { key: "name", label: "Name" },
    { key: "number", label: "Certificate No." },
    { key: "detail", label: "Detail", full: true },
    { key: "validity", label: "Validity" },
  ],
};

const LOCATION_LIST = {
  emptyItem: { city: "", state: "", type: "presence", detail: "" },
  fields: [
    { key: "city", label: "City" },
    { key: "state", label: "State" },
    { key: "type", label: "Type", options: ["presence", "plant", "hq"] },
    { key: "detail", label: "Detail", required: false },
  ],
};

const NEWS_LIST = {
  emptyItem: { date: "", tag: "UPDATE", title: "", summary: "", body: "" },
  fields: [
    { key: "date", label: "Date (YYYY-MM-DD)" },
    { key: "tag", label: "Tag (AWARD / INNOVATION / …)" },
    { key: "title", label: "Title", full: true },
    { key: "summary", label: "Summary", full: true, textarea: true },
    { key: "body", label: "Full story", full: true, textarea: true, required: false },
  ],
};

function ProductsTab() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/products").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const startEdit = (p) =>
    setEditing(p ? { ...p, applicationsCsv: (p.applications || []).join(", "), tablesJson: JSON.stringify(p.tables || [], null, 2) } : { ...EMPTY_PRODUCT });

  const save = async () => {
    let tables;
    try {
      tables = JSON.parse(editing.tablesJson || "[]");
    } catch {
      toast.error("Spec tables JSON is invalid");
      return;
    }
    const payload = {
      name: editing.name, category: editing.category, standard: editing.standard,
      packaging: editing.packaging, description: editing.description,
      applications: editing.applicationsCsv.split(",").map((s) => s.trim()).filter(Boolean),
      tables,
    };
    try {
      if (editing.id) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("Product saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    await api.delete(`/admin/products/${p.id}`);
    toast.success("Product deleted");
    load();
  };

  if (!items) return <p className="font-mono text-xs text-neutral-500">Loading…</p>;

  if (editing) {
    const setF = (k) => (e) => setEditing((ed) => ({ ...ed, [k]: e.target.value }));
    return (
      <div className="border border-white/10 bg-[#0C0C0C] p-6" data-testid="product-editor">
        <h3 className="font-display text-xl font-semibold uppercase tracking-normal">{editing.id ? "Edit Product" : "New Product"}</h3>
        <div className="mt-6 grid gap-5 sm:grid-cols-2">
          <div><label className={labelCls}>Name</label><input className={inputCls} value={editing.name} onChange={setF("name")} data-testid="product-edit-name" /></div>
          <div>
            <label className={labelCls}>Category</label>
            <select className={inputCls} value={editing.category} onChange={setF("category")} data-testid="product-edit-category">
              {["Bituminous Products", "Specialty Petrochemicals", "Industrial Materials", "Fuels & Others"].map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div><label className={labelCls}>Standard</label><input className={inputCls} value={editing.standard} onChange={setF("standard")} data-testid="product-edit-standard" /></div>
          <div><label className={labelCls}>Packaging</label><input className={inputCls} value={editing.packaging} onChange={setF("packaging")} data-testid="product-edit-packaging" /></div>
          <div className="sm:col-span-2"><label className={labelCls}>Description</label><textarea rows={3} className={inputCls} value={editing.description} onChange={setF("description")} data-testid="product-edit-description" /></div>
          <div className="sm:col-span-2"><label className={labelCls}>Applications (comma separated)</label><input className={inputCls} value={editing.applicationsCsv} onChange={setF("applicationsCsv")} data-testid="product-edit-applications" /></div>
          <div className="sm:col-span-2"><label className={labelCls}>Spec tables (JSON)</label><textarea rows={8} className={`${inputCls} font-mono text-xs`} value={editing.tablesJson} onChange={setF("tablesJson")} data-testid="product-edit-tables" /></div>
        </div>
        <div className="mt-6 flex gap-3">
          <button onClick={save} data-testid="product-save-btn" className="inline-flex items-center gap-2 bg-brand px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white hover:bg-brand-deep"><Save className="h-3.5 w-3.5" /> Save</button>
          <button onClick={() => setEditing(null)} data-testid="product-cancel-btn" className="border border-white/15 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400 hover:border-brand hover:text-brand">Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="products-tab">
      <button onClick={() => startEdit(null)} data-testid="product-add-btn" className="mb-6 inline-flex items-center gap-2 bg-brand px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white hover:bg-brand-deep">
        <Plus className="h-3.5 w-3.5" /> Add Product
      </button>
      <div className="space-y-3">
        {items.map((p) => (
          <div key={p.id} className="flex flex-wrap items-center gap-4 border border-white/10 bg-[#0C0C0C] px-5 py-4">
            <div>
              <p className="font-medium text-white">{p.name}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-500">{p.category} · {p.standard}</p>
            </div>
            <div className="ml-auto flex gap-2">
              <button onClick={() => startEdit(p)} data-testid={`product-edit-${p.id}`} className="border border-white/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-300 hover:border-brand hover:text-brand">Edit</button>
              <button onClick={() => remove(p)} data-testid={`product-delete-${p.id}`} className="border border-white/15 p-2 text-neutral-500 hover:border-brand hover:text-brand"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SimpleListTab({ endpoint, fields, emptyItem, rowTitle, rowSub, testPrefix }) {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(emptyItem);
  const load = () => api.get(`/${endpoint}`).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, [endpoint]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/${endpoint}`, form);
      toast.success("Added");
      setForm(emptyItem);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (item) => {
    if (!window.confirm("Delete this entry?")) return;
    await api.delete(`/admin/${endpoint}/${item.id}`);
    load();
  };

  if (!items) return <p className="font-mono text-xs text-neutral-500">Loading…</p>;

  return (
    <div data-testid={`${testPrefix}-tab`}>
      <form onSubmit={add} className="mb-8 grid gap-4 border border-white/10 bg-[#0C0C0C] p-6 sm:grid-cols-2" data-testid={`${testPrefix}-add-form`}>
        {fields.map((f) => (
          <div key={f.key} className={f.full ? "sm:col-span-2" : ""}>
            <label className={labelCls}>{f.label}</label>
            {f.textarea ? (
              <textarea rows={2} required={f.required !== false} className={inputCls} value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`${testPrefix}-input-${f.key}`} />
            ) : f.options ? (
              <select className={inputCls} value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`${testPrefix}-input-${f.key}`}>
                {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : (
              <input required={f.required !== false} className={inputCls} value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`${testPrefix}-input-${f.key}`} />
            )}
          </div>
        ))}
        <div className="sm:col-span-2">
          <button type="submit" data-testid={`${testPrefix}-add-btn`} className="inline-flex items-center gap-2 bg-brand px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white hover:bg-brand-deep">
            <Plus className="h-3.5 w-3.5" /> Add
          </button>
        </div>
      </form>
      <div className="space-y-3">
        {items.map((item) => (
          <div key={item.id} className="flex items-center gap-4 border border-white/10 bg-[#0C0C0C] px-5 py-4">
            <div>
              <p className="font-medium text-white">{rowTitle(item)}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-500">{rowSub(item)}</p>
            </div>
            <button onClick={() => remove(item)} data-testid={`${testPrefix}-delete-${item.id}`} className="ml-auto border border-white/15 p-2 text-neutral-500 hover:border-brand hover:text-brand">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function MediaTab() {
  const [items, setItems] = useState(null);
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState("");
  const [category, setCategory] = useState("plant");
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/gallery").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose an image first");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", caption);
      fd.append("category", category);
      await api.post("/admin/gallery/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo published to the site");
      setFile(null);
      setCaption("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm("Remove this photo from the site?")) return;
    await api.delete(`/admin/gallery/${item.id}`);
    load();
  };

  return (
    <div data-testid="media-tab">
      <form onSubmit={upload} className="mb-8 grid gap-4 border border-white/10 bg-[#0C0C0C] p-6 sm:grid-cols-2" data-testid="media-upload-form">
        <div>
          <label className={labelCls}>Photo (JPG / PNG / WEBP, max 10 MB)</label>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className={`${inputCls} file:mr-4 file:border-0 file:bg-brand file:px-4 file:py-2 file:font-mono file:text-[10px] file:uppercase file:text-white`}
            data-testid="media-file-input"
          />
        </div>
        <div>
          <label className={labelCls}>Caption</label>
          <input className={inputCls} value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="e.g. Jhajjar PMB plant" data-testid="media-caption-input" />
        </div>
        <div>
          <label className={labelCls}>Category</label>
          <select className={inputCls} value={category} onChange={(e) => setCategory(e.target.value)} data-testid="media-category-select">
            {["plant", "drum-yard", "port", "operations", "event"].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="flex items-end">
          <button type="submit" disabled={uploading} data-testid="media-upload-btn" className="inline-flex items-center gap-2 bg-brand px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white hover:bg-brand-deep disabled:opacity-50">
            <Plus className="h-3.5 w-3.5" /> {uploading ? "Uploading…" : "Upload Photo"}
          </button>
        </div>
      </form>
      {!items ? (
        <p className="font-mono text-xs text-neutral-500">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-neutral-500" data-testid="media-empty">No photos yet — upload your first plant, drum-yard or port photo above.</p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="media-grid">
          {items.map((g) => (
            <div key={g.id} className="border border-white/10 bg-[#0C0C0C]">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${g.storage_path}`}
                alt={g.caption || g.original_filename}
                className="h-44 w-full object-cover"
              />
              <div className="flex items-center gap-3 px-4 py-3">
                <div>
                  <p className="text-sm text-white">{g.caption || g.original_filename}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-500">{g.category}</p>
                </div>
                <button onClick={() => remove(g)} data-testid={`media-delete-${g.id}`} className="ml-auto border border-white/15 p-2 text-neutral-500 hover:border-brand hover:text-brand">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const EMPTY_RATE = { grade: "", price: "", unit: "per MT", note: "" };

function RatesTab() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(null); // null = list view, object = editing/adding
  const load = () => api.get("/market/rates").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (form.id) await api.put(`/admin/rates/${form.id}`, form);
      else await api.post("/admin/rates", form);
      toast.success("Rate published to the site");
      setForm(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete rate for ${item.grade}?`)) return;
    await api.delete(`/admin/rates/${item.id}`);
    load();
  };

  if (!items) return <p className="font-mono text-xs text-neutral-500">Loading…</p>;

  if (form) {
    const setF = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    return (
      <form onSubmit={save} className="grid gap-5 border border-white/10 bg-[#0C0C0C] p-6 sm:grid-cols-2" data-testid="rate-editor">
        <div className="sm:col-span-2"><label className={labelCls}>Grade / Product</label><input required className={inputCls} value={form.grade} onChange={setF("grade")} placeholder="e.g. Bitumen VG-30 — Bulk, Ex-Plant" data-testid="rate-grade-input" /></div>
        <div><label className={labelCls}>Price</label><input required className={inputCls} value={form.price} onChange={setF("price")} placeholder="e.g. ₹52,400 or On Request" data-testid="rate-price-input" /></div>
        <div><label className={labelCls}>Unit</label><input className={inputCls} value={form.unit} onChange={setF("unit")} data-testid="rate-unit-input" /></div>
        <div className="sm:col-span-2"><label className={labelCls}>Note</label><input className={inputCls} value={form.note} onChange={setF("note")} placeholder="e.g. Ex-Jhajjar, GST extra" data-testid="rate-note-input" /></div>
        <div className="flex gap-3 sm:col-span-2">
          <button type="submit" data-testid="rate-save-btn" className="inline-flex items-center gap-2 bg-brand px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white hover:bg-brand-deep"><Save className="h-3.5 w-3.5" /> Publish</button>
          <button type="button" onClick={() => setForm(null)} data-testid="rate-cancel-btn" className="border border-white/15 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400 hover:border-brand hover:text-brand">Cancel</button>
        </div>
      </form>
    );
  }

  return (
    <div data-testid="rates-tab">
      <p className="mb-4 text-sm text-neutral-500">Update these every morning — they appear on the public News → Market Wire rate board with an &ldquo;updated&rdquo; timestamp.</p>
      <button onClick={() => setForm({ ...EMPTY_RATE })} data-testid="rate-add-btn" className="mb-6 inline-flex items-center gap-2 bg-brand px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white hover:bg-brand-deep">
        <Plus className="h-3.5 w-3.5" /> Add Rate
      </button>
      <div className="space-y-3">
        {items.map((r) => (
          <div key={r.id} className="flex flex-wrap items-center gap-4 border border-white/10 bg-[#0C0C0C] px-5 py-4">
            <div>
              <p className="font-medium text-white">{r.grade}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-500">
                <span className="text-brand">{r.price}</span> {r.unit} · updated {new Date(r.updated_at).toLocaleString("en-IN")}
              </p>
            </div>
            <div className="ml-auto flex gap-2">
              <button onClick={() => setForm({ ...r })} data-testid={`rate-edit-${r.id}`} className="border border-white/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-300 hover:border-brand hover:text-brand">Edit</button>
              <button onClick={() => remove(r)} data-testid={`rate-delete-${r.id}`} className="border border-white/15 p-2 text-neutral-500 hover:border-brand hover:text-brand"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("Enquiries");

  useEffect(() => {
    if (user === false) navigate("/admin/login");
  }, [user, navigate]);

  if (!user) return <div className="min-h-screen bg-[#0A0A0A]" data-testid="admin-loading" />;

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
    navigate("/admin/login");
  };

  return (
    <main className="min-h-screen bg-[#0A0A0A] px-6 py-8 md:px-12" data-testid="admin-dashboard">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-wrap items-center gap-4 border-b border-white/10 pb-6">
          <img src="/logo-mark.png" alt="Catalyst Petrochem logo" className="h-9 w-auto" />
          <h1 className="font-display text-2xl font-bold uppercase tracking-normal">
            Catalyst <span className="text-brand">CMS</span>
          </h1>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">{user.email}</p>
          <div className="ml-auto flex gap-3">
            <Link to="/" data-testid="admin-view-site" className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-300 hover:border-brand hover:text-brand">
              <ExternalLink className="h-3.5 w-3.5" /> View site
            </Link>
            <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-300 hover:border-brand hover:text-brand">
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-2" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`admin-tab-${t.toLowerCase()}`}
              className={`border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                tab === t ? "border-brand bg-brand text-white" : "border-white/15 text-neutral-400 hover:border-brand hover:text-brand"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="mt-8 pb-20">
          {tab === "Enquiries" && <EnquiriesTab />}
          {tab === "Content" && <ContentTab />}
          {tab === "Products" && <ProductsTab />}
          {tab === "Achievements" && (
            <SimpleListTab
              endpoint="achievements"
              testPrefix="achievement"
              emptyItem={ACHIEVEMENT_LIST.emptyItem}
              fields={ACHIEVEMENT_LIST.fields}
              rowTitle={(a) => `${a.year} — ${a.title}`}
              rowSub={(a) => a.tag}
            />
          )}
          {tab === "Certifications" && (
            <SimpleListTab
              endpoint="certifications"
              testPrefix="certification"
              emptyItem={CERTIFICATION_LIST.emptyItem}
              fields={CERTIFICATION_LIST.fields}
              rowTitle={(c) => c.name}
              rowSub={(c) => `${c.number} · ${c.validity}`}
            />
          )}
          {tab === "Locations" && (
            <SimpleListTab
              endpoint="locations"
              testPrefix="location"
              emptyItem={LOCATION_LIST.emptyItem}
              fields={LOCATION_LIST.fields}
              rowTitle={(l) => `${l.city}${l.state ? `, ${l.state}` : ""}`}
              rowSub={(l) => `${l.type}${l.detail ? ` · ${l.detail}` : ""}`}
            />
          )}
          {tab === "News" && (
            <SimpleListTab
              endpoint="news"
              testPrefix="news"
              emptyItem={NEWS_LIST.emptyItem}
              fields={NEWS_LIST.fields}
              rowTitle={(n) => `${n.date} — ${n.title}`}
              rowSub={(n) => n.tag}
            />
          )}
          {tab === "Rates" && <RatesTab />}
          {tab === "Media" && <MediaTab />}
        </div>
      </div>
    </main>
  );
}
